import React, { useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import lod_ from "lodash";

import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";

/**
 * Header actions component for bulk operations on selected assets
 * @param {Object} props
 * @param {Array} props.selectedRows - Array of selected asset objects
 * @param {Function} props.onTrain - Callback for train action
 * @param {Function} props.onExtractMetadatas - Callback for metadata extraction
 * @param {Function} props.onMove - Callback for move action
 * @param {Function} props.onChangeStatus - Callback for status change
 * @param {Function} props.onChangeAccess - Callback for access change
 * @param {Function} props.onDelete - Callback for delete action
 * @param {boolean} props.canMoveFiles - Whether files can be moved
 * @param {Function} props.t - Translation function
 */
const AssetTableActions = ({
	selectedRows = [],
	onTrain,
	onExtractMetadatas,
	onMove,
	onChangeStatus,
	onChangeAccess,
	onDelete,
	canMoveFiles = false,
	t
}) => {
	const [anchorEl, setAnchorEl] = useState(null);

	const handleOpenMenu = useCallback(e => {
		setAnchorEl(e.currentTarget);
	}, []);

	const handleCloseMenu = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const handleAction = useCallback(
		action => {
			handleCloseMenu();
			action();
		},
		[handleCloseMenu]
	);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			<Badge
				badgeContent={selectedRows.length}
				color="error"
				anchorOrigin={{
					vertical: "top",
					horizontal: "right"
				}}
			>
				<MDButton
					size="small"
					disabled={lod_.isEmpty(selectedRows)}
					variant="contained"
					color="dark"
					onClick={handleOpenMenu}
				>
					<Icon>list_alt</Icon>&nbsp;Actions
				</MDButton>
			</Badge>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleCloseMenu}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center"
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center"
				}}
			>
				<MenuItem disabled style={{ opacity: 1 }}>
					<MDTypography variant="caption">{t("FAIQ.TESTING.actions")}</MDTypography>
				</MenuItem>

				{/* Train */}
				<MenuItem disabled={selectedRows.length < 1} onClick={() => handleAction(onTrain)}>
					<Icon fontSize="medium">model_training</Icon>&nbsp;{t("SETTINGS.train")}
				</MenuItem>

				{/* Extract Metadatas */}
				<MenuItem onClick={() => handleAction(onExtractMetadatas)}>
					<Icon fontSize="medium">storage</Icon>&nbsp;
					{t("SOURCES.ACTIONS.extractMetadatas")}
				</MenuItem>

				{/* Move */}
				<MenuItem disabled={!canMoveFiles} onClick={() => handleAction(onMove)}>
					<Icon fontSize="medium">drive_file_move</Icon>&nbsp;{t("SETTINGS.move")}
				</MenuItem>

				{/* Change Status */}
				<MenuItem onClick={() => handleAction(onChangeStatus)}>
					<Icon fontSize="medium">change_circle</Icon>&nbsp;
					{t("ASSETS.ACTIONS.changeStatusButton")}
				</MenuItem>

				{/* Change Access */}
				<MenuItem onClick={() => handleAction(onChangeAccess)}>
					<Icon fontSize="medium">lock</Icon>&nbsp;
					{t("ASSETS.ACTIONS.changeAccessButton")}
				</MenuItem>

				<Divider />

				{/* Delete */}
				<MenuItem onClick={() => handleAction(onDelete)} sx={{ color: "error.main" }}>
					<Icon fontSize="medium">delete</Icon>&nbsp;{t("SETTINGS.delete")}
				</MenuItem>
			</Menu>
		</Box>
	);
};

export default React.memo(AssetTableActions);
